import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

export interface BroadcastRow {
  id: number;
  name: string;
  templateId: number;
  audienceId: number | null;
  audienceLabel: string;
  status: string;
  pauseReason: string | null;
  recipientsResolved: number | null;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  scheduleAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface BroadcastRecipient {
  id: number;
  phone: string;
  status: string;
  batchNo: number;
}

export interface CreateBroadcastInput {
  templateId: number;
  audienceId?: number;
  audienceLabel: string;
  audienceDefinition?: Record<string, unknown>;
  templateVariables?: Record<string, string>;
  /** Media row id (POST /api/v1/media) — required when the template has an image header. */
  headerMediaId?: number;
  scheduleAt?: string;
}

export function useBroadcasts(businessId: string) {
  return useQuery<BroadcastRow[]>({
    queryKey: [businessId, "broadcasts"],
    queryFn: async () => {
      const r = await apiFetch<{ broadcasts: BroadcastRow[] }>(
        `/api/v1/broadcasts?business=${businessId}`,
      );
      return r.broadcasts;
    },
  });
}

export function useBroadcast(id: number | null) {
  return useQuery<{ broadcast: BroadcastRow; recipients: BroadcastRecipient[] }>({
    queryKey: ["broadcast", id],
    enabled: id !== null,
    queryFn: () => apiFetch(`/api/v1/broadcasts/${id}`),
  });
}

export function useCreateBroadcast(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBroadcastInput) =>
      apiFetch<{ id: number; status: string }>(`/api/v1/broadcasts?business=${businessId}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "broadcasts"] }),
  });
}

export function useSendBroadcast(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ resolved: number; sent: number; status: string }>(
        `/api/v1/broadcasts/${id}/send`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "broadcasts"] }),
  });
}
